import { Loader2, PanelLeftOpen, Sparkles, SquarePen } from "lucide-react";
import { AgentPeek, BrandLoader } from "@/components/brand";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton
} from "@/components/ai-elements/conversation";
import { PromptInputProvider } from "@/components/ai-elements/prompt-input";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AttachmentScopeProvider } from "@/features/agents/attachments/AttachmentScopeContext";
import type { AttachmentFilePart } from "@/features/agents/attachments/types";
import { QueuedMessages } from "@/features/agents/components/QueuedMessages";
import {
  elsewherePath,
  useElsewhereThreads
} from "@/features/agents/hooks/useElsewhereThreads";
import {
  activeTurnCopy,
  isSystemNotice,
  lastAssistantMessage,
  pendingAskUserPart,
  PENDING_QUESTION_PLACEHOLDER
} from "@/features/agents/useAgentChat";
import { PreferencesChip } from "@/features/agents/components/PreferencesChip";
import { isOffered, skillsForFocus } from "@/features/agents/skillsForFocus";
import { specAtFocus, type ApprovalDecision } from "@/features/agents/types";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { invalidateMatterStarters } from "@/features/agents/useMatterStarters";
import {
  latestMessageNs,
  unstampedTailId,
  useThreadRead
} from "@/features/agents/hooks/useThreadRead";
import { firmKnowledgeApi } from "@/api/firm-knowledge";
import {
  skillRunSeed,
  useActiveFirmSkills
} from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import { plainSkillRunText } from "@/features/firmKnowledge/skillRunText";
import {
  exploreGroupsForCase,
  starterRunMessage
} from "@/features/automations/board/caseExploreStarters";
import { RunSkillDialog } from "@/features/automations/components/RunSkillDialog";
import { automationsPath } from "@/features/automations/routes";
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";
import {
  setCurrentThreadId,
  useNotifications
} from "@/features/notifications/NotificationsProvider";

import type { ConnectAppsOutput } from "@/features/connectors/types";
import { useCaseIde } from "../context/CaseIdeContext";
import { useTeamMembers } from "../data/useTeamMembers";
import { useIdeLayout } from "../layout/ideLayoutStore";
import { PaneMaximizeButton } from "../layout/PaneMaximizeButton";
import { useCaseVisa } from "../useCaseVisa";
import { workflowDisplayText } from "../workflows/catalog";
import { useWorkflowsOptional } from "../workflows/context/WorkflowsContext";
import { useWorkflowExecutor } from "../workflows/hooks/useWorkflowExecutor";
import { readThreadId } from "../workflows/storage";
import { AgentPanelHeader } from "./components/AgentPanelHeader";
import { ChatMessage } from "./components/ChatMessage";
import { opensReplyTurn } from "./components/replyTurn";
import { Composer, type SlashCommand } from "./components/Composer";
import {
  ConversationsSidebar,
  setSidebarOpen,
  useSidebarOpen
} from "./components/ConversationsSidebar";
import { plainMentions } from "./mentions";
import { LatestTurn } from "./components/LatestTurn";
import { QuestionCard } from "./components/QuestionCard";
import {
  OfferActionsProvider,
  useOfferActions
} from "./offers/OfferActionsContext";
import { RunEventsProvider } from "./runEvents";
import {
  SupportCardsProvider,
  useSupportCards
} from "./support/SupportCardsContext";
import { TurnStatusLine } from "./components/TurnStatusLine";

import type { AskUserAnswers, InvalidateTarget } from "../types";
import { useOpenThreadTabs } from "./hooks/useOpenThreadTabs";
import { useCaseAgentChat } from "./useCaseAgentChat";
import { useCaseStarters } from "./useCaseStarters";
import { useThreadLive } from "./useThreadLive";
import { useTitleFor } from "./useTitleFor";
import {
  useMessageHighlight,
  useThreadDeepLink,
  useThreadInUrl
} from "./useThreadDeepLink";

/** What the case's starters are read from (`case_agent/starters.py`). */
const STARTER_INPUTS: ReadonlySet<InvalidateTarget> = new Set([
  "fs",
  "drafts",
  "forms",
  "case",
  "autocategorize",
  "packet",
  "key_facts"
]);

const IconTip: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/**
 * The right pane: the case agent. One conversation per thread; approvals,
 * background runs and open-artifact requests all flow through the stream.
 * Maximized (⌥⌘4) it takes the workspace, chat-app style: the case's
 * conversations in a sidebar and the transcript and composer in a centered
 * reading column.
 */
export const CaseChatPanel: FC = () => {
  const {
    individualId,
    selection,
    setSelectedText,
    onPlanBuildRequest,
    onSendRequest,
    onRunAutomationRequest,
    requestPrompt,
    onInvalidate
  } = useCaseIde();
  const titleFor = useTitleFor();
  const navigate = useNavigate();
  // Maximized: the whole workspace, a conversations sidebar and a centered column.
  const full = useIdeLayout().focus === "casey";
  const sidebarOpen = useSidebarOpen();

  // The editor clears its selection the moment focus moves to the prompt, so
  // pin the last highlight here until it is sent or dismissed.
  const [pinned, setPinned] = useState<string | null>(null);
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const { members: teamMembers, me } = useTeamMembers();
  const deepLink = useThreadDeepLink();
  const chat = useCaseAgentChat({
    titleFor,
    pinnedText: () => pinnedRef.current,
    me,
    initialThreadId: deepLink.threadId
  });
  // Keep this tab in step with teammates' tabs (thread list, lock, messages).
  useThreadLive(chat);
  // Case Prep workflows send their prompts through this chat.
  useWorkflowExecutor(chat, individualId);
  const workflows = useWorkflowsOptional();
  const queuedWorkflows =
    workflows && chat.threadId && chat.threadId === readThreadId(individualId)
      ? workflows.pending.map((p) => ({ id: p.id, text: p.line }))
      : [];
  // Looking at a chat reads it (syncs the read cursor to the server).
  useThreadRead({
    scope: chat.scope,
    threadId: chat.threadId,
    latestNs: latestMessageNs(chat.messages),
    tailId: unstampedTailId(chat.messages, chat.status === "ready")
  });
  // The header's open chat tabs (a subset of the threads; history has them all).
  const tabs = useOpenThreadTabs(individualId, chat);
  const elsewhere = useElsewhereThreads(
    { kind: "case", id: individualId },
    chat.threads.length
  );
  const deleteThread = useCallback(
    (id: string) => {
      // Closing first picks the neighbouring tab, so the transcript never
      // lands on a thread that is about to disappear.
      tabs.close(id);
      void chat.deleteThread(id);
    },
    [tabs, chat]
  );
  const { models, autoModes, practiceScope, specFor } = useAgentSpecs();
  const { scope: caseScope, starters } = useCaseStarters(individualId);
  useEffect(
    () =>
      onInvalidate((targets) => {
        if (targets.some((t) => STARTER_INPUTS.has(t)))
          invalidateMatterStarters(caseScope);
      }),
    [onInvalidate, caseScope]
  );
  // Mentions: per-thread unread badges; opening a thread clears its mentions
  // and tells the notifications feature not to toast for it.
  const { unreadFor, markAllRead } = useNotifications();
  const unreadByThread = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const n of unreadFor({ individual_id: individualId })) {
      const tid = n.context?.thread_id;
      if (typeof tid === "string") counts[tid] = (counts[tid] ?? 0) + 1;
    }
    return counts;
  }, [unreadFor, individualId]);
  const activeUnread = chat.threadId ? unreadByThread[chat.threadId] ?? 0 : 0;
  useEffect(() => {
    setCurrentThreadId(chat.threadId);
    return () => setCurrentThreadId(null);
  }, [chat.threadId]);
  useThreadInUrl(chat.threadId);
  useEffect(() => {
    if (chat.threadId && activeUnread > 0)
      void markAllRead({ thread_id: chat.threadId });
  }, [chat.threadId, activeUnread, markAllRead]);
  // Opened from a mention: scroll to and briefly highlight the message.
  const highlightedId = useMessageHighlight(
    deepLink.messageId,
    chat.messages,
    chat.restoring
  );
  useEffect(() => {
    if (selection.text) setPinned(selection.text);
  }, [selection.text]);
  useEffect(() => {
    setPinned(null);
  }, [selection.artifact?.id, selection.artifact?.kind]);

  const send = useCallback(
    (text: string, files?: AttachmentFilePart[]) =>
      void (files?.length
        ? chat.send(text, undefined, files)
        : chat.send(text)),
    [chat]
  );

  // Firm skills: the chip pins one on the thread; `/name` does the same from
  // the keyboard; Play (or "Run on this case" from Firm Knowledge, arriving as
  // `?thread=&run=`) records a run and sends its seed message once the thread
  // is the active, restored one.
  const firmSkills = useActiveFirmSkills();
  // The picker lists the firm's saved skills for the case focus, the Explore
  // catalog in the same groups as the Case Automations board, then the
  // assistant's built-in procedures.
  const assistantSpec = specFor("assistant");
  const caseVisa = useCaseVisa();
  const exploreGroups = useMemo(
    () => exploreGroupsForCase(caseVisa || null),
    [caseVisa]
  );
  const skills = useMemo(
    () =>
      skillsForFocus(
        firmSkills,
        assistantSpec ? specAtFocus(assistantSpec, "case").skills : undefined,
        "case",
        caseVisa || null
      ),
    [firmSkills, assistantSpec, caseVisa]
  );
  // "Run on this case" from Firm Knowledge carries the run's seed (with the
  // inputs the attorney gave) in router state; without it the seed is
  // derived from the thread's skill title.
  const location = useLocation();
  const stateSeed =
    (location.state as { skillRunSeed?: string } | null)?.skillRunSeed ?? "";
  const [pendingRun, setPendingRun] = useState<{
    threadId: string;
    seed: string;
  } | null>(
    deepLink.threadId && deepLink.runId
      ? { threadId: deepLink.threadId, seed: stateSeed }
      : null
  );
  const activeThread = chat.threads.find((t) => t.id === chat.threadId);
  useEffect(() => {
    if (!pendingRun || chat.threadId !== pendingRun.threadId) return;
    if (chat.restoring || chat.status !== "ready" || chat.threadsLoading)
      return;
    // A deep-linked run derives its seed from the thread's skill; wait until
    // the thread (with its skill id) and the skill list are both here.
    let { seed } = pendingRun;
    if (!seed) {
      if (!activeThread || activeThread.skill_run_id !== deepLink.runId) return;
      const title = skills.find((s) => s.id === activeThread.skill_id)?.title;
      if (!title) return;
      seed = skillRunSeed(title);
    }
    setPendingRun(null);
    // Never re-send onto a thread that already has its seed (a reload).
    if (chat.messages.length > 0) return;
    void chat.send(plainSkillRunText(seed));
  }, [pendingRun, chat, activeThread, deepLink.runId, skills]);

  // A skill that asks questions first opens them in a dialog before the run.
  const [askingSkill, setAskingSkill] = useState<FirmSkillSummary | null>(null);
  const startSkill = useCallback(
    async (
      skill: FirmSkillSummary,
      inputs: Record<string, string | string[]>,
      mode?: "agent" | "plan"
    ) => {
      try {
        const started = await firmKnowledgeApi.startSkillRun(skill.id, {
          individual_id: individualId,
          inputs,
          ...(mode ? { mode } : {}),
          ...(chat.threadId ? { thread_id: chat.threadId } : {})
        });
        setAskingSkill(null);
        if (started.thread_id === chat.threadId) {
          // The run adopted this thread: pin the chip, send the seed now.
          chat.setSkill(skill.id);
          void chat.send(plainSkillRunText(started.seed_message));
          return;
        }
        // A new thread: open it and send the seed once it has restored.
        setPendingRun({
          threadId: started.thread_id,
          seed: started.seed_message
        });
        await chat.refreshThreads();
        chat.setThreadId(started.thread_id);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Could not start the automation."
        );
      }
    },
    [chat, individualId]
  );
  const runSkill = useCallback(
    (skill: FirmSkillSummary) => {
      // A built-in has no run to record: it rides the seed message.
      if (skill.builtin) {
        chat.setSkill(skill.id);
        void chat.send(skillRunSeed(skill.title));
        return;
      }
      if (skill.inputs?.length) {
        setAskingSkill(skill);
        return;
      }
      void startSkill(skill, {});
    },
    [startSkill, chat]
  );
  // The Case Automations board's Run button for a firm automation.
  useEffect(
    () =>
      onRunAutomationRequest?.((skillId) => {
        const skill = skills.find((s) => s.id === skillId);
        if (skill) runSkill(skill);
      }),
    [onRunAutomationRequest, skills, runSkill]
  );
  const slashCommands = useMemo<SlashCommand[]>(
    () =>
      skills.filter(isOffered).map((s) => ({
        id: `skill-${s.id}`,
        label: s.title,
        hint: s.replaces_title
          ? `Replaces built-in ${s.replaces_title}`
          : s.description,
        group: s.builtin ? "Built-In" : "Firm",
        run: () => (s.builtin ? runSkill(s) : chat.setSkill(s.id))
      })),
    [skills, chat, runSkill]
  );
  const saveAsSkill = useCallback(
    (threadId: string) => navigate(automationsPath({ fromThread: threadId })),
    [navigate]
  );
  // The pinned text rides along in the request body's `selection`. It is
  // released once the request is actually in flight (a first message may wait
  // for its thread to be created), so it is never dropped early.
  useEffect(() => {
    if (chat.status !== "submitted") return;
    setPinned(null);
    setSelectedText(null);
  }, [chat.status, setSelectedText]);

  const onApproval = useCallback(
    (decision: ApprovalDecision) => void chat.respondToApproval(decision),
    [chat]
  );
  const onAnswer = useCallback(
    (toolCallId: string, answers: AskUserAnswers) =>
      void chat.answerQuestions(toolCallId, answers),
    [chat]
  );
  const onConnectResolved = useCallback(
    (toolCallId: string, output: ConnectAppsOutput) =>
      void chat.resolveConnect(toolCallId, output),
    [chat]
  );
  const offerActions = useOfferActions(chat);
  const supportCards = useSupportCards(chat);
  // The plan pane's (and plan card's) Build button lands here.
  useEffect(
    () => onPlanBuildRequest((planId) => void chat.buildPlan(planId)),
    [onPlanBuildRequest, chat]
  );
  useEffect(() => onSendRequest?.((text) => send(text)), [onSendRequest, send]);

  // An `ask_user` question awaiting answers is docked on the composer rather
  // than shown in the transcript.
  const pendingQuestion = useMemo(
    () => (chat.restoring ? null : pendingAskUserPart(chat.messages)),
    [chat.messages, chat.restoring]
  );

  const streaming = chat.status === "streaming" || chat.status === "submitted";
  const lastAssistant = useMemo(
    () => lastAssistantMessage(chat.messages),
    [chat.messages]
  );
  // The reply being streamed: the last assistant message, once it exists and
  // nothing (a user message) has landed after it.
  const streamingMessage =
    streaming &&
    lastAssistant &&
    chat.messages[chat.messages.length - 1]?.id === lastAssistant.id
      ? lastAssistant
      : null;
  // Older replies skip layout and paint until scrolled back into view.
  const settledBefore = Math.max(0, chat.messages.length - 6);
  const onRetry = useCallback(
    (options?: { model?: string }) =>
      void chat.regenerate(
        options?.model ? { body: { model: options.model } } : undefined
      ),
    [chat]
  );
  const onStop = useCallback(() => void chat.stopTurn(), [chat]);
  // A turn running that this client is not streaming: a teammate's, our own
  // from another tab or a reload, or one continued on the worker pool after
  // a server restart. Shown until the chat reattaches to it (`resumeStream`),
  // at which point the live stream takes over.
  const otherTurn = chat.activeTurn && !streaming ? chat.activeTurn : null;
  // The latest turn starts at the last user message; while a question is
  // pending it is anchored at the top of the pane (see LatestTurn).
  const latestTurnStart = useMemo(() => {
    let i = chat.messages.length - 1;
    while (i >= 0) {
      const m = chat.messages[i];
      if (m.role === "user" && !isSystemNotice(m.metadata)) return i;
      i -= 1;
    }
    return 0;
  }, [chat.messages]);

  const renderMessage = (m: (typeof chat.messages)[number], i: number) => {
    const isStreamingReply = streamingMessage?.id === m.id;
    return (
      <ChatMessage
        key={m.id}
        message={m}
        isStreaming={isStreamingReply}
        onApproval={onApproval}
        onAnswer={onAnswer}
        onConnectResolved={onConnectResolved}
        meUid={me.uid || null}
        meAvatarUrl={me.avatarUrl}
        highlighted={highlightedId === m.id}
        steps={isStreamingReply ? chat.turnSteps : undefined}
        onRetry={m.id === lastAssistant?.id && !streaming ? onRetry : undefined}
        settled={i < settledBefore && highlightedId !== m.id}
        showByline={opensReplyTurn(chat.messages, i)}
        canSaveToWorkProducts
      />
    );
  };

  const turnStatus = (
    <>
      {streaming ? (
        <TurnStatusLine message={streamingMessage} onStop={onStop} />
      ) : null}
      {otherTurn ? (
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-testid="other-turn"
        >
          <Loader2 className="size-3 animate-spin" />
          {activeTurnCopy(otherTurn, "the agent", me.uid)}…
        </div>
      ) : null}
    </>
  );
  const hasTranscript = !chat.restoring && chat.messages.length > 0;

  return (
    // The column keeps its place in both layouts, so maximizing never
    // remounts the transcript or the composer.
    <div className="flex h-full min-h-0">
      {full && sidebarOpen ? (
        <ConversationsSidebar
          threads={chat.threads}
          activeId={chat.threadId}
          workflowThreadId={readThreadId(individualId)}
          unreadByThread={unreadByThread}
          disabled={chat.threadsLoading}
          onSelect={chat.setThreadId}
          onNew={chat.startNewThread}
        />
      ) : null}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {full ? (
          <div
            className="flex h-9 shrink-0 items-center gap-1 px-2"
            data-testid="casey-full-header"
          >
            {sidebarOpen ? null : (
              <>
                <IconTip label="Show Conversations">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-fg-3 hover:text-fg"
                    aria-label="Show Conversations"
                    onClick={() => setSidebarOpen(true)}
                  >
                    <PanelLeftOpen className="size-4" />
                  </Button>
                </IconTip>
                <IconTip label="New Chat">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-fg-3 hover:text-fg"
                    aria-label="New Chat"
                    disabled={chat.threadsLoading}
                    onClick={chat.startNewThread}
                  >
                    <SquarePen className="size-4" />
                  </Button>
                </IconTip>
              </>
            )}
            <span className="min-w-0 flex-1 truncate px-1 text-base font-medium text-fg">
              {activeThread
                ? plainMentions(workflowDisplayText(activeThread.title)) ||
                  "New Chat"
                : "New Chat"}
            </span>
            <PaneMaximizeButton focus="casey" />
          </div>
        ) : (
          <AgentPanelHeader
            threads={chat.threads}
            openThreads={tabs.openThreads}
            activeId={chat.threadId}
            onSelect={chat.setThreadId}
            onNew={chat.startNewThread}
            onClose={tabs.close}
            onCloseOthers={tabs.closeOthers}
            onRename={(id, title) => void chat.renameThread(id, title)}
            onDelete={deleteThread}
            onSaveAsSkill={saveAsSkill}
            disabled={chat.threadsLoading}
            unreadByThread={unreadByThread}
            elsewhere={elsewhere}
            onOpenElsewhere={(t) => navigate(elsewherePath(t))}
            actions={<PaneMaximizeButton focus="casey" />}
          />
        )}

        {chat.restoring ? (
          <div className="flex min-h-0 flex-1 items-center justify-center p-3">
            <BrandLoader size="md" label="Restoring conversation" />
          </div>
        ) : (
          <Conversation className="min-h-0 flex-1">
            <ConversationContent
              className={cn(
                full ? "mx-auto w-full max-w-3xl gap-6 px-6 py-6" : "gap-4 p-3"
              )}
            >
              {chat.messages.length === 0 ? (
                <ConversationEmptyState
                  icon={<AgentPeek ledge className="-mb-1.5 w-20" />}
                  title="Casey can help"
                  description="It can organize files, draft, assemble the packet, fill forms and run research. Destructive steps wait for your approval."
                />
              ) : (
                <AttachmentScopeProvider scope={chat.scope}>
                  <RunEventsProvider messages={chat.messages}>
                    <OfferActionsProvider value={offerActions}>
                      <SupportCardsProvider value={supportCards}>
                        {chat.messages
                          .slice(0, latestTurnStart)
                          .map(renderMessage)}
                        <LatestTurn
                          anchor={pendingQuestion !== null}
                          className="not-first:mt-2"
                        >
                          {chat.messages
                            .slice(latestTurnStart)
                            .map((m, j) =>
                              renderMessage(m, latestTurnStart + j)
                            )}
                          {turnStatus}
                        </LatestTurn>
                      </SupportCardsProvider>
                    </OfferActionsProvider>
                  </RunEventsProvider>
                </AttachmentScopeProvider>
              )}
              {hasTranscript ? null : turnStatus}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>
        )}

        <div
          className={cn(
            "shrink-0 space-y-2",
            full ? "mx-auto w-full max-w-3xl px-6 pt-2 pb-4" : "p-2",
            // The docked question is the separator; no rule above its tab.
            !pendingQuestion && !full && "border-t border-border"
          )}
          data-testid="casey-composer-area"
        >
          {chat.error ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
              <span className="truncate">{chat.error}</span>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => void chat.regenerate()}
                >
                  Retry
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={chat.clearError}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ) : null}
          {chat.messages.length === 0 && !chat.restoring ? (
            <Suggestions>
              {starters.map((s) => (
                <Suggestion
                  key={s}
                  suggestion={s}
                  onClick={send}
                  className="h-6 rounded-full px-2.5 text-xs"
                />
              ))}
              {firmSkills
                .filter((s) => isOffered(s) && s.id !== chat.skillId)
                .map((s) => (
                  <Suggestion
                    key={`skill-${s.id}`}
                    suggestion={`Use ${s.title}`}
                    onClick={() => {
                      chat.setSkill(s.id);
                      requestPrompt("");
                    }}
                    className="h-6 rounded-full px-2.5 text-xs"
                    data-testid={`use-skill-${s.id}`}
                  >
                    <Sparkles className="size-3 text-purple" />
                    Use {s.title}
                  </Suggestion>
                ))}
            </Suggestions>
          ) : null}
          {/*
          Queued rows tuck under the box; an open question is a tab on the box
          itself, so the queue sits above it with its own margin.
        */}
          <div>
            <QueuedMessages
              items={chat.queue.items}
              workflows={queuedWorkflows}
              onRemoveWorkflow={(id) => {
                const step = workflows?.pending.find((p) => p.id === id);
                if (step) workflows?.dequeue(step.id);
              }}
              steering={chat.steerInbox}
              paused={chat.queue.paused}
              meUid={me.uid || null}
              onEdit={(id, text) => void chat.queue.edit(id, text)}
              onRemove={(id) => void chat.queue.remove(id)}
              onReorder={(ids) => void chat.queue.reorder(ids)}
              onSendNow={(id) => void chat.queue.sendNow(id)}
              onResume={chat.queue.resume}
              className={pendingQuestion ? "mb-2 pb-1" : undefined}
            />
            {pendingQuestion ? (
              <div
                className="max-h-[50vh] overflow-y-auto"
                data-testid="docked-question"
              >
                <QuestionCard
                  docked
                  part={pendingQuestion}
                  onAnswer={onAnswer}
                />
              </div>
            ) : null}
            <PromptInputProvider>
              <Composer
                status={chat.status}
                disabled={chat.threadsLoading || chat.restoring}
                onSend={send}
                onStop={() => void chat.stopTurn()}
                onQueue={(text, files) => void chat.queue.enqueue(text, files)}
                onSendNow={(text, files) =>
                  void chat.sendNow(text, undefined, files)
                }
                attachmentScope={chat.scope}
                pinnedSelection={pinned}
                onClearPinnedSelection={() => {
                  setPinned(null);
                  setSelectedText(null);
                }}
                mode={chat.mode}
                onModeChange={(m) => void chat.setMode(m)}
                skills={skills}
                skillId={chat.skillId}
                onSkillChange={chat.setSkill}
                skillPinned={chat.skillPinned}
                onSkillPinChange={chat.setSkillPinned}
                preferencesControl={
                  <PreferencesChip
                    scope={chat.scope}
                    threadId={chat.threadId}
                    refreshKey={chat.status === "ready"}
                  />
                }
                onRunSkill={runSkill}
                exploreGroups={exploreGroups}
                onRunStarter={(starter) => send(starterRunMessage(starter))}
                firmName={practiceScope?.displayName}
                slashCommands={slashCommands}
                models={models}
                autoModes={autoModes}
                model={chat.model}
                thinking={chat.thinking}
                optimizeFor={chat.optimizeFor}
                onModelChange={chat.setModel}
                onThinkingChange={chat.setThinking}
                onOptimizeForChange={chat.setOptimizeFor}
                draftKey={`case:${individualId}:${chat.threadId ?? "new"}`}
                teamMembers={teamMembers}
                meUid={me.uid || null}
                activeTurn={chat.activeTurn}
                followUp={chat.messages.length > 0}
                placeholder={
                  pendingQuestion ? PENDING_QUESTION_PLACEHOLDER : undefined
                }
              />
            </PromptInputProvider>
          </div>
        </div>
      </div>
      <RunSkillDialog
        open={askingSkill !== null}
        onOpenChange={(open) => {
          if (!open) setAskingSkill(null);
        }}
        skill={
          askingSkill
            ? {
                title: askingSkill.title,
                inputs: askingSkill.inputs ?? [],
                defaultMode: askingSkill.default_mode ?? "agent"
              }
            : null
        }
        needsCase={false}
        onStart={({ inputs, mode }) =>
          askingSkill ? startSkill(askingSkill, inputs, mode) : undefined
        }
      />
    </div>
  );
};
